import { useEffect, useRef, useState } from "react";
import { C } from "../lib/theme";
import { Icon } from "./Icon";
import { supabase } from "../lib/supabase";

// Two-step phone verification for desktop:
//   Step 1 — enter phone, request SMS OTP via Supabase Auth
//            (supabase.auth.updateUser({ phone }) triggers a phone_change OTP).
//   Step 2 — enter 6-digit code, verify via supabase.auth.verifyOtp({ type: 'phone_change' }),
//            then mirror phone + phone_verified=true onto public.users so RLS
//            and admin views can rely on the same source of truth as mobile.
//
// After success, callers should invoke useAuth().refresh() so the TopNav and
// Overview cards immediately reflect the verified phone.
//
// Note: Supabase project must have an SMS provider configured in Auth settings
// (Twilio / MessageBird / Vonage). If it isn't, the "Send code" step surfaces
// the provider error verbatim so the user knows to try again later.
function normalizePhone(raw) {
  const trimmed = (raw || "").trim().replace(/\s+/g, "");
  if (!trimmed) return "";
  if (trimmed.startsWith("+")) return trimmed;
  // Zambia default. Strip a leading 0 so "0977..." → "+260977..." like mobile.
  const stripped = trimmed.replace(/^0+/, "");
  return `+260${stripped}`;
}

export function PhoneVerificationModal({ open, onClose, userId, initialPhone, onSuccess }) {
  const [step, setStep] = useState("enter");
  const [phone, setPhone] = useState(initialPhone || "");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const codeRefs = useRef([]);

  useEffect(() => {
    if (!open) {
      setStep("enter");
      setCode(["", "", "", "", "", ""]);
      setBusy(false);
      setError(null);
      setInfo(null);
      setPhone(initialPhone || "");
    }
  }, [open, initialPhone]);

  const normalized = normalizePhone(phone);
  const canSend = /^\+\d{10,15}$/.test(normalized) && !busy;
  const codeJoined = code.join("");
  const canVerify = codeJoined.length === 6 && !busy;

  async function sendCode(e) {
    e?.preventDefault?.();
    if (!canSend) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    const { error: err } = await supabase.auth.updateUser({ phone: normalized });
    setBusy(false);
    if (err) {
      setError(err.message || "Could not send verification code. Try again.");
      return;
    }
    setInfo(`We sent a 6-digit code to ${normalized}.`);
    setStep("verify");
    // Focus first code input on the next tick after render.
    setTimeout(() => codeRefs.current[0]?.focus(), 50);
  }

  async function verifyCode(e) {
    e?.preventDefault?.();
    if (!canVerify || !userId) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.verifyOtp({
      phone: normalized,
      token: codeJoined,
      type: "phone_change",
    });
    if (err) {
      setBusy(false);
      setError(err.message || "Invalid code. Please try again.");
      return;
    }
    // Auth succeeded — mirror into public.users so the rest of the app can
    // trust `phone_verified` without re-decoding the auth user.
    const { error: userErr } = await supabase
      .from("users")
      .update({ phone: normalized, phone_verified: true })
      .eq("id", userId);
    setBusy(false);
    if (userErr) {
      setError("Phone verified, but profile update failed. Please refresh.");
      return;
    }
    onSuccess?.();
  }

  function handleCodeChange(idx, val) {
    const clean = val.replace(/\D/g, "").slice(0, 1);
    setCode((prev) => {
      const next = [...prev];
      next[idx] = clean;
      return next;
    });
    if (clean && idx < 5) codeRefs.current[idx + 1]?.focus();
  }

  function handleCodeKey(idx, e) {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      codeRefs.current[idx - 1]?.focus();
    }
  }

  function handleCodePaste(e) {
    const pasted = (e.clipboardData || window.clipboardData)?.getData("text") || "";
    const digits = pasted.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < digits.length; i += 1) next[i] = digits[i];
    setCode(next);
    const focusIdx = Math.min(digits.length, 5);
    codeRefs.current[focusIdx]?.focus();
  }

  async function resend() {
    setBusy(true);
    setError(null);
    setInfo(null);
    const { error: err } = await supabase.auth.updateUser({ phone: normalized });
    setBusy(false);
    if (err) {
      setError(err.message || "Could not resend code.");
      return;
    }
    setInfo("New code sent.");
  }

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(28,28,28,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          background: C.bg,
          borderRadius: 20,
          boxShadow: "0 30px 80px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: C.white,
            borderBottom: "1px solid rgba(196,193,183,0.3)",
          }}
        >
          <div>
            <div style={{ fontSize: 17, fontWeight: 900, color: C.charcoal, letterSpacing: "-0.3px" }}>
              Verify your phone
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
              {step === "enter" ? "We'll send a 6-digit code via SMS." : "Enter the 6-digit code you received."}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6 }}
          >
            <Icon name="x" size={18} color={C.charcoal} />
          </button>
        </div>

        {step === "enter" ? (
          <form onSubmit={sendCode} style={{ padding: 22, display: "grid", gap: 14 }}>
            <label style={{ display: "block" }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: C.slate, marginBottom: 6, letterSpacing: 0.2 }}>
                Phone number
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 0977 123 456"
                required
                style={inputStyle}
                autoFocus
              />
              {normalized && normalized !== phone.trim() && (
                <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>
                  Will send to {normalized}
                </div>
              )}
            </label>

            {error && <ErrorBox message={error} />}
            {info && <InfoBox message={info} />}

            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" onClick={onClose} disabled={busy} style={cancelBtnStyle(busy)}>
                Cancel
              </button>
              <button type="submit" disabled={!canSend} style={primaryBtnStyle(canSend)}>
                {busy ? "Sending…" : "Send code"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={verifyCode} style={{ padding: 22, display: "grid", gap: 14 }}>
            {info && <InfoBox message={info} />}

            <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
              {code.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (codeRefs.current[i] = el)}
                  value={d}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKey(i, e)}
                  onPaste={i === 0 ? handleCodePaste : undefined}
                  inputMode="numeric"
                  maxLength={1}
                  style={{
                    flex: 1,
                    height: 52,
                    fontSize: 20,
                    fontWeight: 800,
                    textAlign: "center",
                    background: C.cream,
                    border: "1px solid rgba(196,193,183,0.4)",
                    borderRadius: 12,
                    color: C.charcoal,
                    outline: "none",
                    fontFamily: "inherit",
                  }}
                />
              ))}
            </div>

            {error && <ErrorBox message={error} />}

            <button type="button" onClick={resend} disabled={busy} style={ghostLinkStyle(busy)}>
              Didn't get it? Resend code
            </button>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => {
                  setStep("enter");
                  setError(null);
                  setInfo(null);
                }}
                disabled={busy}
                style={cancelBtnStyle(busy)}
              >
                Back
              </button>
              <button type="submit" disabled={!canVerify} style={primaryBtnStyle(canVerify)}>
                {busy ? "Verifying…" : "Verify phone"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        background: C.redBg,
        border: `1px solid ${C.red}`,
        borderRadius: 10,
        fontSize: 12.5,
        color: C.red,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}

function InfoBox({ message }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        background: C.greenBg,
        border: `1px solid ${C.hunter}55`,
        borderRadius: 10,
        fontSize: 12.5,
        color: C.hunter,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "11px 13px",
  fontSize: 13.5,
  fontFamily: "inherit",
  background: C.cream,
  border: "1px solid rgba(196,193,183,0.4)",
  borderRadius: 12,
  color: C.charcoal,
  outline: "none",
  boxSizing: "border-box",
};

function cancelBtnStyle(busy) {
  return {
    flex: 1,
    padding: "12px 16px",
    border: `1px solid ${C.dustDk}`,
    background: C.white,
    color: C.charcoal,
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 800,
    cursor: busy ? "not-allowed" : "pointer",
    fontFamily: "inherit",
  };
}

function primaryBtnStyle(enabled) {
  return {
    flex: 2,
    padding: "12px 16px",
    border: "none",
    background: enabled ? C.hunter : C.dustDk,
    color: C.dust,
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 800,
    cursor: enabled ? "pointer" : "not-allowed",
    fontFamily: "inherit",
  };
}

function ghostLinkStyle(busy) {
  return {
    all: "unset",
    cursor: busy ? "not-allowed" : "pointer",
    fontSize: 12,
    fontWeight: 700,
    color: C.hunter,
    textAlign: "center",
  };
}
